import { useMemo, useState } from 'react'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { Stack, Highlight } from '@mantine/core'
import { tableDefaultAlertBannerProps, tableDefaultProps } from '@shared/components/table/table'
import { NoDataMessage } from '@shared/components/table/table-no-data-message'
import { formatDate } from '@shared/helpers/format-date/format-date'
import { DocumentModel } from '@grower-api-types'
import { DocumentViewButton } from '@/components/shared/document-view-button/document-view-button'
import classes from './documents-table.module.css'

interface DocumentsTableProps {
  data: DocumentModel[]
  noDataMessage?: string
  isLoading?: boolean
  isError?: boolean
}

/**
 * DocumentsTable component renders a table displaying document data with various columns
 * and supports features like sorting, filtering, and custom rendering for specific columns.
 *
 * Can be used to display general resource documents, or specific documents related to a station.
 *
 * @param {DocumentsTableProps} props - The properties for the DocumentsTable component.
 * @param {DocumentModel[]} [props.data=[]] - The data to be displayed in the table.
 * @param {string} [props.noDataMessage='No documents available'] -
 * The message to display when no data is available.
 * @param {boolean} props.isLoading - Indicates whether the table is in a loading state.
 * @param {boolean} props.isError - Indicates whether there is an error state for the table.
 *
 * @returns {JSX.Element} A MantineReactTable component configured with the provided data and columns.
 */
export const DocumentsTable = ({
  data = [],
  noDataMessage = 'No documents available',
  isLoading,
  isError,
}: DocumentsTableProps) => {
  // State to track the search value
  const [globalFilter, setGlobalFilter] = useState('')

  const columns = useMemo<MRT_ColumnDef<DocumentModel>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Document',
        accessorFn: (row) => row.title || row.fileName,
        Cell: ({ row }) => (
          <Stack gap='xs'>
            <Highlight size='md' fw={500} highlight={globalFilter} className={classes.title}>
              {row.original.title || row.original.fileName || ''}
            </Highlight>
            <Highlight highlight={globalFilter} size='sm' className={classes.description}>
              {row.original.description || ''}
            </Highlight>
          </Stack>
        ),
        enableSorting: true,
        grow: true,
        minSize: 300,
      },
      {
        id: 'lastModified',
        accessorKey: 'lastModified',
        header: 'Last Modified',
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY'),
        enableSorting: true,
        size: 120,
      },
      {
        id: 'type',
        accessorKey: 'type',
        header: 'Type',
        Cell: ({ cell }) => (
          <Highlight highlight={globalFilter} size='sm'>
            {cell.getValue<string>() || ''}
          </Highlight>
        ),
        enableSorting: true,
      },
    ],
    [globalFilter]
  )
  const table = useMantineReactTable({
    ...tableDefaultProps,
    columns,
    data,
    state: {
      isLoading,
      globalFilter,
      showAlertBanner: isError,
    },
    initialState: {
      ...tableDefaultProps.initialState,
      sorting: [
        {
          id: 'name',
          desc: false,
        },
      ],
      showGlobalFilter: true,
      columnPinning: { right: ['mrt-row-actions'] },
    },
    mantineToolbarAlertBannerProps: tableDefaultAlertBannerProps(isError),
    renderEmptyRowsFallback: () => <NoDataMessage noDataMessage={noDataMessage} />,
    mantineTableProps: {
      ...tableDefaultProps.mantineTableProps,
      'aria-label': 'Documents table',
    },
    // Pin important columns
    // Hide all internal actions buttons, only show searchbox
    renderToolbarInternalActions: () => <></>,
    enableColumnPinning: true,
    // Sorting
    enableSorting: true,
    // Searching
    enableFilters: true,
    enableColumnFilters: false,
    enableGlobalFilter: true,
    enableFilterMatchHighlighting: false,
    mantineSearchTextInputProps: {
      'aria-label': 'Search documents',
    },
    onGlobalFilterChange: setGlobalFilter,
    // Actions
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => <DocumentViewButton relativeUrl={row.original.relativeUrl} />,
    // Other
    enableDensityToggle: false,
    enableFullScreenToggle: false,
  })

  const tableKey = columns.map((col) => col.id).join('-')

  return <MantineReactTable table={table} key={tableKey} />
}
